import { useEffect, useState } from 'react';

import { subscribeToTeamMembers } from '../firebase/teamService';
import { useHorizontalCarousel } from '../hooks/useHorizontalCarousel';
import { captureException } from '../lib/monitoring';
import type { TeamMember } from '../types/domain';

type Status = 'loading' | 'ready' | 'error';

/* ─── Constantes de layout ──────────────────────────────────────────────── */
const CARD_WIDTH = 280;   // px — ancho de la tarjeta
const CARD_GAP   = 24;    // px — gap entre tarjetas

/* ─── MemberCard ─────────────────────────────────────────────────────────── */
interface MemberCardProps {
  m: TeamMember;
}

function MemberCard({ m }: MemberCardProps) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const hasPhoto    = Boolean(m.photoUrl) && !photoFailed;
  const cardHeight  = Math.round(CARD_WIDTH * 1.25); // aspect 4/5

  return (
    <div
      className="flex-shrink-0 flex flex-col group hover:-translate-y-1.5 transition-transform duration-400"
      style={{ width: `${CARD_WIDTH}px` }}
    >
      {/* Foto / placeholder */}
      <div
        className="rounded-2xl mb-6 overflow-hidden relative isolate"
        style={{
          width: `${CARD_WIDTH}px`,
          height: `${cardHeight}px`, // aspect 4/5
          background: 'linear-gradient(155deg, #EEF1F8 0%, #F8F9FB 60%)',
          flexShrink: 0,
        }}
      >
        {hasPhoto ? (
          <img
            src={m.photoUrl}
            alt={m.name}
            loading="lazy"
            decoding="async"
            width={CARD_WIDTH}
            height={cardHeight}
            onError={() => setPhotoFailed(true)}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          /* Fallback: iniciales sobre gradiente cuando no hay foto o falló la carga */
          <div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center font-heading text-etribe-blue/40 text-[64px] font-bold select-none"
          >
            {m.name
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase() ?? '')
              .join('')}
          </div>
        )}

        {/* Número */}
        <span className="absolute top-4 left-4 font-heading text-xs tracking-[0.18em] text-etribe-blue bg-white/85 backdrop-blur px-2.5 py-1.5 rounded-md z-10 font-bold">
          {m.num}
        </span>

        {/* Indicador activo */}
        <span className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full bg-etribe-yellow shadow-[0_0_0_4px_rgba(255,200,54,.18)] z-10" />

        {/* Overlay hover */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-etribe-blue/45 opacity-0 group-hover:opacity-100 transition-opacity duration-400 z-0 pointer-events-none"
        />
      </div>

      {/* Info */}
      <p className="font-heading text-[20px] tracking-[0.02em] mb-1.5 uppercase leading-tight">{m.name}</p>
      <div className="text-etribe-ink-2 text-[13px] font-medium mb-4 pb-3.5 relative">
        {m.role}
        <span className="absolute left-0 bottom-0 w-7 h-0.5 bg-etribe-yellow group-hover:w-14 transition-all" />
      </div>
      <p className="text-etribe-ink-2 text-[13px] leading-[1.55]">{m.bio}</p>
    </div>
  );
}

/* ─── Team Section ───────────────────────────────────────────────────────── */
export default function Team() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [status,  setStatus]  = useState<Status>('loading');

  // Carousel state — extraído al hook compartido.
  const {
    trackRef,
    canPrev,
    canNext,
    onScroll,
    scrollByStep,
    scrollToIndex,
    isDotActive,
  } = useHorizontalCarousel({
    cardWidth: CARD_WIDTH,
    cardGap: CARD_GAP,
    resetKey: members.length,
  });

  // Suscripción a Firebase
  useEffect(() => {
    const unsubscribe = subscribeToTeamMembers((data, err) => {
      if (err) {
        captureException(err, { section: 'team', source: 'subscribeToTeamMembers' });
        setMembers([]);
        setStatus('error');
        return;
      }
      setMembers(data);
      setStatus('ready');
    });
    return () => unsubscribe();
  }, []);

  // Si está cargando, hay error o no hay miembros, no renderizamos la sección
  if (status !== 'ready' || members.length === 0) return null;

  return (
    <section id="equipo" className="py-[120px]">
      <div className="container-etribe">

        {/* Encabezado + controles de navegación */}
        <div className="flex items-end justify-between mb-16 gap-6 flex-wrap">
          <div className="max-w-[720px]">
            <p className="eyebrow"><span>La tribu</span></p>
            <h2 className="text-[clamp(32px,3.6vw,48px)] mb-4">Conoce a quienes están detrás</h2>
            <p className="text-[clamp(17px,1.3vw,20px)] text-etribe-ink-2 leading-[1.55]">
              Directivos cercanos que se sientan al lado del cliente. Sin capas intermedias, sin discursos vacíos.
            </p>
          </div>

          {/* Flechas — solo aparecen cuando hay overflow */}
          {(canPrev || canNext) && (
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => scrollByStep(-1)}
                disabled={!canPrev}
                aria-label="Anterior miembro del equipo"
                className="w-11 h-11 rounded-full border border-etribe-line flex items-center justify-center text-etribe-blue transition-all duration-200 hover:bg-etribe-blue hover:text-white hover:border-etribe-blue disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-etribe-blue disabled:hover:border-etribe-line"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M19 12H5M11 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => scrollByStep(1)}
                disabled={!canNext}
                aria-label="Siguiente miembro del equipo"
                className="w-11 h-11 rounded-full border border-etribe-line flex items-center justify-center text-etribe-blue transition-all duration-200 hover:bg-etribe-blue hover:text-white hover:border-etribe-blue disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-etribe-blue disabled:hover:border-etribe-line"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Carousel track — scrollbar oculto cross-browser */}
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="team-carousel-track flex overflow-x-auto pb-6 mb-4"
          style={{
            gap: `${CARD_GAP}px`,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
          aria-label="Lista de miembros del equipo"
        >
          {members.map((m) => <MemberCard key={m.id} m={m} />)}
        </div>

        {/* Indicadores de puntos */}
        {members.length > 1 && (
          <div className="flex items-center justify-center gap-2 mb-16">
            {members.map((m, i) => {
              const isActive = isDotActive(i);
              return (
                <button
                  key={m.id}
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Ir a ${m.name}`}
                  aria-current={isActive ? 'true' : undefined}
                  className={`rounded-full transition-all duration-300 ${
                    isActive
                      ? 'w-6 h-2 bg-etribe-blue'
                      : 'w-2 h-2 bg-etribe-line hover:bg-etribe-blue/40'
                  }`}
                />
              );
            })}
          </div>
        )}

        {/* Quote de cierre */}
        <div className="relative max-w-[920px] mx-auto mt-8 group">
          {/* Glow ambiental detrás */}
          <div
            aria-hidden
            className="absolute inset-0 -z-10 rounded-[28px] opacity-70"
            style={{
              background:
                'radial-gradient(60% 60% at 50% 50%, rgba(255,200,54,.10), transparent 70%)',
            }}
          />

          <div className="relative text-center py-20 px-8 sm:px-14 rounded-[28px] bg-gradient-to-br from-etribe-blue/[0.03] via-white to-etribe-blue/[0.02] border border-etribe-line shadow-[0_30px_60px_-30px_rgba(16,3,163,.25)] overflow-hidden transition-all duration-500 hover:shadow-[0_40px_80px_-30px_rgba(16,3,163,.35)] hover:-translate-y-1">

            {/* Comilla decorativa gigante */}
            <span
              aria-hidden
              className="absolute top-2 left-6 font-heading font-black text-etribe-yellow/25 text-[180px] leading-none select-none pointer-events-none transition-all duration-700 group-hover:text-etribe-yellow/40 group-hover:scale-110"
            >
              &ldquo;
            </span>
            <span
              aria-hidden
              className="absolute -bottom-12 right-6 font-heading font-black text-etribe-yellow/25 text-[180px] leading-none select-none pointer-events-none transition-all duration-700 group-hover:text-etribe-yellow/40 group-hover:scale-110"
            >
              &rdquo;
            </span>

            {/* Líneas decorativas amarillas */}
            <span
              aria-hidden
              className="absolute top-1/2 -left-2 w-12 h-px bg-gradient-to-r from-transparent to-etribe-yellow"
            />
            <span
              aria-hidden
              className="absolute top-1/2 -right-2 w-12 h-px bg-gradient-to-l from-transparent to-etribe-yellow"
            />

            <blockquote className="relative font-heading text-[clamp(24px,2.6vw,38px)] text-etribe-blue leading-[1.25] tracking-[0.015em]">
              No buscamos clientes.
              <br />
              Buscamos{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-etribe-blue">compañeros de viaje digital</span>
                <span
                  aria-hidden
                  className="absolute left-0 bottom-1 w-full h-3 bg-etribe-yellow/55 -z-0 transition-all duration-500 group-hover:h-4 group-hover:bg-etribe-yellow/70"
                />
              </span>
              .
            </blockquote>
          </div>
        </div>

      </div>
    </section>
  );
}
